// components/NodeEditor.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import ColorPickerButton from './fragments/ColorPickerButton'
import FormField from './FormField'
import { overlays } from './nodes/featureOverlay/overlayRegistry';
import { useApi } from '../context/ApiContext/ApiContext';
// import { SettingsContext } from '../context/SettingsContext/SettingsContext'; // Adjust path
import { useSettings } from '../context/SettingsContext/SettingsContext'; // Adjust path
import { handleCopyStyle, handlePasteNodeStyle, handlePasteEdgeStyle } from '../utils/copyToClipboard'


  // shared styles object for consistency across components
  export const styles = {
    // Common colors
    colors: {
      primary: '#FB1FA7',          // Pink
      secondary: '#392EC0',        // Indigo/purple
      darkSecondary: '#200457',    // Dark purple (used in headers)
      darkHover: '#301467',        // Hover state for dark purple
      accent: '#22425e',           // Blue accent
      accentHover: '#c8047d',      // Hover state for accent
      
      // Background colors
      bgDark: 'rgba(17,24,39,0.5)',    // Dark with opacity
      bgPurple: 'rgba(57,46,192,0.5)', // Purple with opacity
      bgOverlay: 'rgba(0,0,0,0.3)',    // Overlay background with opacity
      
      // Border colors
      borderMain: 'rgba(57,46,192,0.7)',
      borderSecondary: '#302ec0',
      borderDark: 'rgba(75,85,99,0.6)', // gray-600 with opacity
      
      // Text colors
      textPrimary: '#ffffff',
      textSecondary: '#e5e7eb', // gray-200
      textMuted: '#9ca3af',     // gray-400
    },
    
    // Common effects
    effects: {
      backdropBlur: 'backdrop-blur-sm',
      // gradientBg: 'bg-gradient-to-r from-[rgba(17,24,39,0.7)] to-[rgba(57,46,192,0.7)]',
      gradientBg: 'bg-gradient-to-r from-[hsla(221,39%,9%,0.8)] to-[hsla(245,61%,30%,0.8)]',
      boxShadow: 'shadow-xl',
    },
    
    // Common container styles
    containers: {
      panel: 'backdrop-blur-sm bg-gradient-to-r from-[rgba(17,24,39,0.5)] to-[rgba(57,46,192,0.5)] border border-gray-600 rounded-lg shadow-xl',
      header: 'py-1 text-sm w-full text-center font-medium border-b rounded-tr-md border-gray-500 bg-[rgba(32,4,87,0.5)]',
      section: 'bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-xl p-3 border border-gray-600',
    },
    
    // Common button styles
    buttons: {
      primary: 'flex items-center justify-center rounded-md px-4 py-2 text-white font-semibold transition-all border border-gray-500 duration-200 bg-[#22425e] hover:bg-[#c8047d]',
      secondary: 'flex items-center justify-center rounded-md px-4 py-2 text-white font-semibold transition-all border border-gray-500 duration-200 bg-[#200457] hover:bg-[#301467]',
      disabled: 'bg-gray-600 cursor-not-allowed',
    }
  };

const NodeEditor = ({ colorOptions, selectedNode, setSelectedNode, onUpdateNode, selectedEdge, onUpdateEdge, updateNodeInternals, nodes, setNodes, setEdges, isLocked, copiedStyle, setCopiedStyle }) => {
  const { 
    // state,
    // setUplynkCentral,
    // updateUplynkCentral,
    // appendUplynkJobState,
    // clearUplynkCentral,
    // moduleNodes, 
    // executionOrder, 
    // executionStatus,
    // setExecutionOrder,
    // reorderExecution,
    // toggleModuleActive,
    // setExecutionStatus,
    // updateSharedParams,
    removeModule,
  } = useApi();
  const { settings, updateSetting, resetAllSettings, replaceAllSettings, logCurrentSettings } = useSettings();
  const [originalNodeSettings, setOriginalNodeSettings] = useState(null);
  const [originalEdgeSettings, setOriginalEdgeSettings] = useState(null);
  const [newNodeSettings, setNewNodeSettings] = useState({});
  const [newEdgeSettings, setNewEdgeSettings] = useState({});
  const [nodePropertyChanges, setNodePropertyChanges] = useState(false);
  const [edgePropertyChanges, setEdgePropertyChanges] = useState(false);
  const [previousNodeId, setPreviousNodeId] = useState(null);
  const [previousEdgeId, setPreviousEdgeId] = useState(null);
  
  // const [resetPosition, setResetPosition] = useState(null);
  

  // Marker styling options
  const markerTypes = [
    { label: 'Arrow Closed', value: 'arrowclosed' },
    { label: 'Arrow', value: 'arrow' },
    { label: 'None', value: '' }
  ];

  // Connector Types
  const connectorTypes = [
    { label: 'Bezier', value: 'bezier' },
    { label: 'Smooth Step', value: 'smoothstep' },
    { label: 'Straight', value: 'straight' },
  ];

   // Border styles
   const borderStyleOptions = [
    { label: 'Solid', value: 'solid' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Dotted', value: 'dotted' },
    { label: 'Double', value: 'double' },
  ];

  // Text Weight
  const fontWeightOptions = [
    { label: 'Normal', value: 'normal' },
    { label: 'Medium', value: '500' },
    { label: 'Semi Bold', value: '600' },
    { label: 'Bold', value: 'bold' },
    { label: 'Extra Bold', value: '800' },
  ];

  // Handles: "Input" & "Output"
  const inOutHandlePositions = [
    { label: 'top', value: 'top' },
    { label: 'bottom', value: 'bottom' },
    { label: 'left', value: 'left' },
    { label: 'right', value: 'right' },
    { label: 'none', value: 'none' },
  ];

  // Icons
  const iconStyleOptions = [
    { label: 'None', value: 'none' },
    { label: 'Broadcast Tower', value: 'broadcastTower' },
    { label: 'Signal', value: 'signal' },
    { label: 'IP', value: 'ip' },
    { label: 'WIFI', value: 'wifi' },
    { label: 'Satellite', value: 'satellite' },
    { label: 'Satellite Dish', value: 'satelliteDish' },
    { label: 'Camera', value: 'camera' },
    { label: 'Microphone', value: 'microphone' },
    { label: 'File', value: 'file' },
    { label: 'File Video', value: 'fileVideo' },
    { label: 'Cloud Upload', value: 'cloudUpload' },
    { label: 'Ads', value: 'ads' },
    { label: 'Cog', value: 'encoder' },
    { label: 'Cycle', value: 'cycle' },
    { label: 'Server', value: 'server' },
    { label: 'Security', value: 'security' },
    { label: 'Data Centre', value: 'dataCentre' },
    { label: 'Operator Cog', value: 'operatorCog' },
    { label: 'Operator Headset', value: 'operatorHeadset' },
    { label: 'Control Room', value: 'controlroom' },
    { label: 'Cloud', value: 'cloud' },
    { label: 'Video Player', value: 'videoPlayer' },
    { label: 'Person', value: 'person' },
    { label: 'Streaming', value: 'streaming' },
    { label: 'Mobile Phone', value: 'mobilePhone' },
    { label: 'Desktop', value: 'desktop' },
    { label: 'TV', value: 'tv' },
    { label: 'STB', value: 'stb' },
    { label: 'Newspaper', value: 'newspaper' },
    { label: 'Robot', value: 'ai' },
    { label: 'Globe', value: 'globe' },
    { label: 'Announcer', value: 'bullhorn' },
    { label: 'Apple', value: 'apple' },
    { label: 'Google', value: 'google' },
    { label: 'Twitch', value: 'twitch' },
    { label: 'TikTok', value: 'tiktok' },
    { label: 'Facebook', value: 'facebook' },
    { label: 'Instagram', value: 'instagram' },
    { label: 'YouTube', value: 'youtube' },
    { label: 'Arrow Down', value: 'arrowDown' },
    { label: 'Arrow Left', value: 'arrowLeft' },
    { label: 'Arrow Right', value: 'arrowRight' },
    { label: 'Arrow Up', value: 'arrowUp' },
    { label: 'Equals', value: 'equals' },
    { label: 'Finger Print', value: 'fingerprint' },
    { label: 'UI', value: 'borderNone' },
    { label: 'Uplynk', value: 'uplynk' },
    { label: 'Zixi', value: 'zixi' },
    { label: 'Bitmovin', value: 'bitmovin' },
    { label: 'GoogleClr', value: 'googlecolor' },
    { label: 'Gemini', value: 'gemini' },
    { label: 'Oracle', value: 'oracle' },
    { label: 'Akamai', value: 'akamai' },
    { label: 'x', value: 'x' }
  ];

  // Objects Position Options
  // const verticalAlignOptions = useMemo(() => {
  //   const isColumn = newNodeSettings?.style?.flexDirection === 'column';
  //   return [
  //     { label: isColumn ? 'Left' : 'Top', value: 'flex-start' },
  //     { label: 'Center', value: 'center' },
  //     { label: isColumn ? 'Right' : 'Bottom', value: 'flex-end' },
  //   ];
  // }, [newNodeSettings?.style?.flexDirection]);
  

  // Handles - Validation function (Prevent In / Out handle in same possition)
  const validatePositions = (sourcePosition, targetPosition) => {
    if (sourcePosition === 'none' || targetPosition === 'none') {
      return { sourcePosition, targetPosition };
    }
  
    if (sourcePosition === targetPosition) {
      const validOptions = {
        top: ['bottom', 'left', 'right'],
        bottom: ['top', 'left', 'right'],
        left: ['top', 'bottom', 'right'],
        right: ['top', 'bottom', 'left']
      };
      return { sourcePosition, targetPosition: validOptions[sourcePosition][0] };
    }
    return { sourcePosition, targetPosition };
  };


  // Duplicate Selected Node
  const handleDuplicateNode = () => {
    if (!selectedNode) return;
  
    // Create a deep copy of the newNodeSettings (which includes the latest changes)
    const nodeCopy = JSON.parse(JSON.stringify(newNodeSettings));
  
    // Generate new ID
    const newId = uuidv4();
  
    // Update position, maintaining x but offsetting y by 50
    const newPosition = {
      x: selectedNode.position.x,
      y: selectedNode.position.y + 60
    };
  
    // Update node label
    const newLabel = `${nodeCopy.label} copy`;
  
    // Create new node with updated properties
    const newNode = {
      ...selectedNode, // Use the original node structure
      id: newId,
      position: newPosition,
      // positionAbsolute: newPosition,
      data: {
        ...nodeCopy, // Use the updated settings from newNodeSettings
        label: newLabel
      },
      selected: false  // Ensure original node stays selected
    };
  
    // Add new node to flow
    setNodes(prevNodes => [...prevNodes, newNode]);
  };  
  

  // Send Node to Back - Adjust Node Order
  const changeDisplayOrder = (nodeId, order) => {
    setNodes((prevNodes) => {
      const nodeToModify = prevNodes.find((node) => node.id === nodeId);
      if (!nodeToModify) return prevNodes;
  
      const otherNodes = prevNodes.filter((node) => node.id !== nodeId);
      const reorderedNodes = order === 'back' ? [nodeToModify, ...otherNodes] : [...otherNodes, nodeToModify];
      
      // Clear selected state from all nodes
      return reorderedNodes.map(node => ({ ...node, selected: false }));
    });
    setSelectedNode(null);
  };

  // NODE Changes / Update
  const handleNodeChange = (key, value) => {
    if (key === 'feature') {
      console.log("** handleNodeChange - key === feature", key);
      removeModule(selectedNode.id)
    }
    const updatedSettings = { ...newNodeSettings };
    // console.log('selectedNode.data.nodeWidthHeight.current', selectedNode.data.nodeWidthHeight.current);

    if (key === 'titleDescSpacing') {
      console.log(1.3 * (1 + newNodeSettings?.titleDescSpacing));
    }

    if (key.startsWith('style.')) {
      // Handle nested updates for style properties
      const styleKey = key.split('.')[1];

      // Handle numeric values with units
      if (['fontSize', 'padding', 'borderRadius'].includes(styleKey)) {
        value = `${value}px`;
      };

      updatedSettings.style = {
        ...updatedSettings.style,
        [styleKey]: value,
      };
      // Remove the composite border property if it exists
      delete updatedSettings.style.border;
    } else {
      // General updates for top-level properties
      updatedSettings[key] = value;
    }
  
    // Handle position changes and edge cleanup
    if (key === 'sourcePosition' || key === 'targetPosition') {
      // Delete connected edges when handle positions are set to 'none'
      if (value === 'none') {
        setEdges(prevEdges => prevEdges.filter(edge => {
          if (key === 'sourcePosition' && edge.source === selectedNode.id) return false;
          if (key === 'targetPosition' && edge.target === selectedNode.id) return false;
          return true;
        }));
      }
      
      // Validate positions
      const { sourcePosition, targetPosition } = updatedSettings;
      const validatedPositions = validatePositions(sourcePosition, targetPosition);
      updatedSettings.sourcePosition = validatedPositions.sourcePosition;
      updatedSettings.targetPosition = validatedPositions.targetPosition;
    }
  
    // Sync updated settings with state and callbacks
    setNewNodeSettings(updatedSettings);
  
    if (selectedNode) {
      onUpdateNode({
        ...selectedNode,
        data: updatedSettings,
      });
    }
  
    // Update internals when positions change
    if (selectedNode && (key === 'sourcePosition' || key === 'targetPosition')) {
      updateNodeInternals(selectedNode.id);
    }
    console.log("updatedSettings", updatedSettings);
  };

  // Edge Changes
  const handleEdgeChange = (key, value) => {
    const updatedSettings = { ...newEdgeSettings };
    if (key.startsWith('style.')) {
      // Handle nested updates for style properties
      const styleKey = key.split('.')[1];

      updatedSettings.style = {
        ...updatedSettings.style,
        [styleKey]: value,
      };

    } else if (key.startsWith('markerEnd.')) {
      // Handle updates for markerEnd properties, including "size" for both width and height
      const markerKey = key.split('.')[1];
      updatedSettings.markerEnd = {
        ...updatedSettings.markerEnd,
        ...(markerKey === 'size'
          ? { width: value, height: value } // If size is updated, set both width and height
          : { [markerKey]: value }), // Otherwise, update the specific key
      };
    } else {
      if (key === 'animated') {
        if (value) {
          updatedSettings.animated = true;
          updatedSettings.style.strokeDasharray = '5';
        } else {
          updatedSettings.animated = false;
        }
      } else if (key === 'connectorType') {
        updatedSettings.data.connectorType = value;
      } else {
        // General updates for top-level properties
        updatedSettings[key] = value;
      }
    }
    
    // Sync updated settings with state and callbacks
    setNewEdgeSettings(updatedSettings);
    console.log("selectedEdge", selectedEdge);
    console.log("updatedSettings", updatedSettings);
    if (selectedEdge) {
      onUpdateEdge({
        ...selectedEdge,
        ...updatedSettings,
      });
    }
    // updateNodeInternals(selectedEdge.id);
    updateNodeInternals(selectedEdge.source);
    updateNodeInternals(selectedEdge.target);  
  };

  // UNDO
  const handleUndo = () => {
    if (originalEdgeSettings && selectedEdge) {
      setNewEdgeSettings(originalEdgeSettings);
      onUpdateEdge({
        ...selectedEdge,
        ...originalEdgeSettings,
      });
      
      // Update internals of both connected nodes
      updateNodeInternals(selectedEdge.source);
      updateNodeInternals(selectedEdge.target);
      
      setNodePropertyChanges(false);
    } else if (originalNodeSettings && selectedNode) {
      // Existing node handling
      setNewNodeSettings(originalNodeSettings);
      onUpdateNode({
        ...selectedNode,
        data: originalNodeSettings,
      });
      
      // Update node internals if needed
      if (originalNodeSettings.sourcePosition || originalNodeSettings.targetPosition) {
        updateNodeInternals(selectedNode.id);
      }
      
      setEdgePropertyChanges(false);
    }
  };


  // SELECT NODE / EDGE - Load settings when "selectedNode" or "selectedEdge" changes
  useEffect(() => {
    if (isLocked) return;

    if (selectedNode && selectedNode.id !== previousNodeId) {
      // Reset Edge Memory States
      setEdgePropertyChanges(false);
      setNewEdgeSettings({});
      setOriginalEdgeSettings(null);
      setPreviousEdgeId(null);
      ///

      // Update the ref with the selected node's dimensions
      if (selectedNode.data.nodeWidthHeight) {
        selectedNode.data.nodeWidthHeight.current = [
          selectedNode.data.style.width,
          selectedNode.data.style.height,
        ];
      }

      setPreviousNodeId(selectedNode.id);
      
      // Extract and validate positions
      const { sourcePosition = 'right', targetPosition = 'left' } = selectedNode.data;
      const validatedPositions = validatePositions(sourcePosition, targetPosition);

      // Extract individual border properties from composite border if needed
      const style = selectedNode.data.style || {};
      const defaultStyle = {
        borderWidth: '3px',
        borderStyle: 'solid',
        borderColor: '#808080',
        ...style
      };

      const settings = { 
        ...selectedNode.data, 
        ...validatedPositions,
        style: defaultStyle
      };

      // if (!newNodeSettings || selectedNode.id !== previousNodeId) {
        setNewNodeSettings(settings);
        setOriginalNodeSettings(settings);
        setNodePropertyChanges(false);
      // }
      
    // } else if (selectedEdge) {
    } else if (selectedEdge && selectedEdge.id !== previousEdgeId) {
      // Reset Node Memory States
      setNodePropertyChanges(false);
      setNewNodeSettings({});
      setOriginalNodeSettings(null);
      setPreviousNodeId(null);
      ///

      setPreviousEdgeId(selectedEdge.id);
      // console.log('Edge Selected', selectedEdge);
      // Only set these if we're selecting a different node
      
      // const settings = { ...selectedEdge };
      const settings = JSON.parse(JSON.stringify(selectedEdge));
      // if (!newEdgeSettings || selectedEdge.id !== previousEdgeId) {
        setNewEdgeSettings(settings);
        setOriginalEdgeSettings(settings);
        setEdgePropertyChanges(false);
      // }
    }
  }, [selectedNode, selectedEdge]);
  
  // Track Changes and set State
  useEffect(() => {
    if (!nodePropertyChanges && originalNodeSettings) {
      // Deep compare the objects, ignoring any undefined/null values
      const hasChanges = !isEqual(
        JSON.parse(JSON.stringify(originalNodeSettings)), 
        JSON.parse(JSON.stringify(newNodeSettings))
      );
      
      if (hasChanges) {
        setNodePropertyChanges(true);
      }
    } else if (!edgePropertyChanges && originalEdgeSettings) {
      // Deep compare the objects, ignoring any undefined/null values
      const hasChanges = !isEqual(
        JSON.parse(JSON.stringify(originalEdgeSettings)), 
        JSON.parse(JSON.stringify(newEdgeSettings))
      );
      
      if (hasChanges) {
        setEdgePropertyChanges(true);
      }
    }
  }, [newNodeSettings, originalNodeSettings, newEdgeSettings, originalEdgeSettings, nodePropertyChanges, edgePropertyChanges]);


  const renderNodeEditor = () => (
    <div className="flex flex-row">
      {/* Left Column */}
      {/* <div className="pb-2 text-sm min-w-96 w-[32em] border-r border-b border-[rgba(75,85,99,0.6)] backdrop-blur-sm bg-gradient-to-r from-[rgba(17,24,39,0.5)] to-[rgba(57,46,192,0.5)] shadow-xl"> */}
      <div className={`pb-2 min-w-96 w-[32em] border-r border-b border-[${styles.colors.borderDark}] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} rounded-tl-lg ${styles.effects.boxShadow}`}>
      {/* <div className={`pb-2 min-w-96 w-[32em] border-r border-b border-[${styles.colors.borderDark}] ${styles.effects.backdropBlur} rounded-tl-lg ${styles.effects.boxShadow}`}> */}

        {/* Title */}
        <h3 className='p-1 pl-2 text-sm w-full text-left font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.5)]'
        >
          Node Properties:{' '}
          <span
            className="ml-2 py-0.5 px-3 rounded-md border-x-2" 
            style={{
              borderColor: newNodeSettings?.style?.borderColor || '#808080',
              backgroundColor: newNodeSettings?.style?.backgroundColor || '#1f2937',
              color: newNodeSettings?.style?.color || '#ffffff',
            }}
          >
            {newNodeSettings?.hasOwnProperty('label') ? newNodeSettings?.label : '--'}
          </span>
        </h3>
  
        <div className='pt-2 space-y-2 px-1'>
          {/* Title */}
          <FormField
            type="text"
            label="Title"
            value={newNodeSettings.label || ''}
            onChange={(value) => handleNodeChange('label', value)}
          />
        
          {/* Description */}
          <FormField
            type="textarea"
            label="Description"
            value={newNodeSettings.description || ''}
            onChange={(value) => handleNodeChange('description', value)}
          />
  
          {/* Icon Type */}
          <FormField
            type="select"
            label="Icon Style"
            value={newNodeSettings.icon || 'none'}
            onChange={(value) => handleNodeChange('icon', value)}
            options={iconStyleOptions}
          />
  
          {/* Connection Points */}
          <div className="grid grid-cols-2 gap-4">
            {/* Input Position */}
            <FormField
              type="select"
              label="Input"
              value={newNodeSettings.targetPosition || 'left'}
              onChange={(value) => handleNodeChange('targetPosition', value)}
              options={inOutHandlePositions}
              disabledOptions={newNodeSettings.sourcePosition !== 'none' ? [newNodeSettings.sourcePosition || 'left'] : []}
            />
  
            {/* Output Position */}
            <FormField
              type="select"
              label="Output"
              value={newNodeSettings.sourcePosition || 'right'}
              onChange={(value) => handleNodeChange('sourcePosition', value)}
              options={inOutHandlePositions}
            />
          </div>
       
          {/* Border Controls */}
          <FormField
            type="range"
            label="Border Width"
            value={newNodeSettings?.style?.borderWidth !== undefined ? parseInt(newNodeSettings?.style?.borderWidth) : 3}
            onChange={(value) => handleNodeChange('style.borderWidth', `${value}px`)}
            min={0}
            max={40}
            step={1}
            unit=""
            className="w-full"
          />
  
          {/* Border Radius Control */}
          <FormField
            type="range"
            label="Border Radius"
            value={newNodeSettings?.style?.borderRadius !== undefined ? parseInt(newNodeSettings?.style?.borderRadius) : 10}
            onChange={(value) => handleNodeChange('style.borderRadius', `${value}`)}
            min={0}
            max={150}
            step={1}
            unit=""
            className="w-full"
          />
  
          {/* Border Style Dropdown */}
          <FormField
            type="select"
            label="Border Style"
            value={newNodeSettings?.style?.borderStyle || 'solid'}
            onChange={(value) => handleNodeChange('style.borderStyle', value)}
            options={borderStyleOptions}
          />
        </div>
      </div>
  
      {/* Colors Column */}
      {/* <div className="border-r border-b border-[rgba(75,85,99,0.6)] backdrop-blur-sm bg-gradient-to-r from-[rgba(17,24,39,0.5)] to-[rgba(57,46,192,0.5)] shadow-xl"> */}
      <div className={`border-r border-b border-[${styles.colors.borderDark}] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} ${styles.effects.boxShadow}`}>

        <h3 className='text-sm py-1 w-full text-center font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.5)]'>Color Options</h3>
        {/* Colors Section */}
        <div className="flex-col pt-2 w-72 h-full">
          <div className="px-2 space-y-1.5 w-full">
            <ColorPickerButton
              handleChange={handleNodeChange}
              colorOptions={colorOptions}
              property="style.backgroundColor"
              displayName="Background Color"
              currentColor={newNodeSettings.style?.backgroundColor}
            />
            <ColorPickerButton
              handleChange={handleNodeChange}
              colorOptions={colorOptions}
              property="style.borderColor"
              displayName="Border Color"
              currentColor={newNodeSettings.style?.borderColor}
            />
            <ColorPickerButton
              handleChange={handleNodeChange}
              colorOptions={colorOptions}
              property="style.color"
              displayName="Title Color"
              currentColor={newNodeSettings.style?.color}
            />
            <ColorPickerButton
              handleChange={handleNodeChange}
              colorOptions={colorOptions}
              property="descFontColor"
              displayName="Desc Color"
              currentColor={newNodeSettings.descFontColor || newNodeSettings.style?.color}
            />
          </div>
          <h3 className='mt-2 py-1 text-sm w-full text-center font-medium border-y border-gray-500 bg-[rgba(32,4,87,0.5)]'>Options</h3>
          <div className="mt-2">
            {/* Arrange Vertical Checkbox */}
            <FormField
              type="checkbox"
              label="Vertical"
              value={newNodeSettings?.style?.flexDirection === 'column'}
              onChange={(checked) =>
                handleNodeChange('style.flexDirection', checked ? 'column' : 'row')
              }
            />
  
            {/* Offset Vertical Content Control */}
            <FormField
              type="range"
              label="OffsetV"
              value={newNodeSettings?.offsetV}
              onChange={(value) => handleNodeChange('offsetV', value)}
              onLabelClick={() => handleNodeChange('offsetV', 0)}
              min={-500}
              max={500}
              step={1}
              unit=""
              className="w-full"
            />             
  
            {/* Offset Horizontal Content Control */}
            <FormField
              type="range"
              label="OffsetH"
              value={newNodeSettings?.offsetH}
              onChange={(value) => handleNodeChange('offsetH', value)}
              onLabelClick={() => handleNodeChange('offsetH', 0)}
              min={-500}
              max={500}
              step={1}
              unit=""
              className="w-full"
            />             
          </div>
        </div>
      </div>
  
      {/* Options Styling Column */}
      {/* <div className="pb-2 border-r border-b border-[rgba(75,85,99,0.6)] backdrop-blur-sm bg-gradient-to-r from-[rgba(17,24,39,0.5)] to-[rgba(57,46,192,0.5)] shadow-xl"> */}
      <div className={`border-r border-b border-[${styles.colors.borderDark}] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} ${styles.effects.boxShadow}`}>
        <h3 className='py-1 text-sm w-full text-center font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.5)]'>Options Cont.</h3>
        <div className="pt-1 pb-2 flex w-72 h-full">
          <div className="pt-1.5 w-full space-y-2.5 px-1">
            {/* Icon Size Control */}
            <FormField
              type="range"
              label="Icon Size"
              value={newNodeSettings?.iconSize !== undefined ? parseInt(newNodeSettings?.iconSize) : 3}
              onChange={(value) => handleNodeChange('iconSize',  value)}
              min={20}
              max={100}
              step={1}
              unit=""
              className="w-full"
            />
  
            {/* Icon Text Gap */}
            <FormField
              type="range"
              label="Icon Spacing"
              value={newNodeSettings?.iconSpacing !== undefined ? parseInt(newNodeSettings?.iconSpacing) : 8}
              onChange={(value) => handleNodeChange('iconSpacing', `${value}px`)}
              onLabelClick={() => handleNodeChange('iconSpacing', 8)}
              min={0}
              max={100}
              step={1}
              unit=""
              className="w-full"
            />
  
            {/* Title Desc Spacing */}
            <FormField
              type="range"
              label="Title Spacing"
              value={parseFloat(newNodeSettings?.titleDescSpacing) || 0}
              onChange={(value) => handleNodeChange('titleDescSpacing', parseFloat(value))}
              onLabelClick={() => handleNodeChange('titleDescSpacing', 0)}
              min={-1.0}
              max={5.0}
              step={0.1}
              unit=""
              className="w-full"
            />
  
            {/* Title Desc Spacing */}
            <FormField
              type="range"
              label="Desc Lines"
              value={parseFloat(newNodeSettings?.descSpacing) || 1.3}
              onChange={(value) => handleNodeChange('descSpacing', parseFloat(value))}
              min={1}
              max={3}
              step={0.1}
              unit=""
              className="w-full"
            />
  
            {/* Title Font Size Control */}
            <FormField
              type="range"
              label="Title Size"
              value={newNodeSettings?.style?.fontSize !== undefined ? parseInt(newNodeSettings?.style?.fontSize) : 14}
              onChange={(value) => handleNodeChange('style.fontSize', value)}
              min={10}
              max={50}
              step={1}
              unit=""
              className="w-full"
            />
  
            {/* Description Font Size Control */}
            <FormField
              type="range"
              label="Desc Size"
              value={newNodeSettings?.descFontSize !== undefined ? parseInt(newNodeSettings?.descFontSize) : newNodeSettings?.style?.fontSize}
              onChange={(value) => handleNodeChange('descFontSize', value)}
              min={10}
              max={50}
              step={1}
              unit=""
              className="w-full"
            />
  
            {/* Title Weight Dropdown */}
            <FormField
              type="select"
              label="Title Weight"
              value={newNodeSettings?.style?.fontWeight || 'bold'}
              onChange={(value) => handleNodeChange('style.fontWeight', value)}
              options={ fontWeightOptions }
              className="text-sm"
            />
  
            {/* overlay Dropdown */}
            <FormField
              type="select"
              label="Feature"
              value={newNodeSettings?.feature}
              onChange={(value) => handleNodeChange('feature', value)}
              options={ overlays }
              className="text-sm max-w-[10.5rem]"
            />
          </div>
        </div>
      </div>
      
      {/* Copy / Paste Style */}
      <div className='flex flex-col'>
        {/* <div className="px-0.5 h-[1.8em] flex flex-col place-items-center justify-center bg-[rgba(0,0,0,0.3)] backdrop-blur-sm border-b border-r border-indigo-800"> */}
        <div className={`border-r border-b border-[${styles.colors.borderDark}] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} ${styles.effects.boxShadow}`}>
          <h3 className='py-1 text-sm w-full text-center font-medium border-b border-indigo-800 bg-[hsla(260,91%,18%,0.8)]'>Clipboard</h3>
          {/* <p className="px-1 text-[0.5em]">Node ID</p> */}
          {/* <p className="px-1 text-[0.5em]">{selectedNode.id}</p> */}
        </div>
        <button
          // onClick={() => handleCopyStyle(selectedNode, "", setCopiedStyle) }
          onClick={() => handleCopyStyle(selectedNode, "", setCopiedStyle, newNodeSettings, null)}
          className={`flex-grow px-3 py-1 text-white text-sm font-medium border-b border-r border-indigo-800 bg-[rgba(32,4,87,0.8)] hover:bg-[#2c067abb] active:bg-[#3e08aa] transition-colors`}
        >
          Copy Style
        </button>
        <button
          // onClick={() => handlePasteNodeStyle(selectedNode, copiedStyle, onUpdateNode, "color")} 
          onClick={() => handlePasteNodeStyle(selectedNode, copiedStyle, onUpdateNode, "color", setNewNodeSettings)} 
          className={`flex-grow px-3 py-1 text-white text-sm font-medium border-b border-r border-indigo-800 bg-[rgba(32,4,87,0.8)] hover:bg-[#2c067abb] active:bg-[#3e08aa] transition-colors`}
        >
          Paste Color
        </button>
        <button
          // onClick={() => handlePasteNodeStyle(selectedNode, copiedStyle, onUpdateNode, "style")} 
          onClick={() => handlePasteNodeStyle(selectedNode, copiedStyle, onUpdateNode, "style", setNewNodeSettings)} 
          className={`flex-grow px-3 py-1 text-white text-sm font-medium border-b border-r border-indigo-800 bg-[rgba(32,4,87,0.8)] hover:bg-[#2c067abb] active:bg-[#3e08aa] transition-colors`}
        >
          Paste Style
        </button>
      </div>

      {/* Miscellaneous Options */}
      <div className='flex flex-col w-56'>
        {/* <div className="px-0.5 h-[1.8em] flex flex-col place-items-center justify-center bg-[rgba(0,0,0,0.3)] backdrop-blur-sm border-b border-r border-indigo-800"> */}
        <div className={`px-0.5 h-[1.8em] flex flex-col place-items-center justify-center bg-[${styles.colors.bgOverlay}] ${styles.effects.backdropBlur} border-b border-r border-indigo-800`}>

          <p className="px-1 text-[0.5em]">Node ID</p>
          <p className="px-1 text-[0.5em]">{selectedNode.id}</p>
        </div>
        <button
          onClick={handleDuplicateNode}
          className={`flex-grow px-3 py-1 text-white text-sm font-medium border-b border-r border-indigo-800 bg-[hsla(260,91%,18%,0.8)] hover:bg-[hsla(260,91%,25%,0.8)] active:hover:bg-[hsla(260,91%,25%)] transition-colors`}
        >
          Duplicate Node
        </button>
        
        <div className='flex-grow text-white text-sm font-medium border-b border-r border-indigo-800'>
          <button
            onClick={() => changeDisplayOrder(selectedNode.id, 'back')}
            className={`flex-1 w-1/2 h-full px-3 py-1 text-white text-sm font-medium border-b border-r border-indigo-800 bg-[hsla(260,91%,18%,0.8)] hover:bg-[hsla(260,91%,25%,0.8)] active:hover:bg-[hsla(260,91%,25%)] transition-colors`}
            >
            {`Node to\nBack`}
          </button>
          <button
            onClick={() => changeDisplayOrder(selectedNode.id, 'front')}
            className={`flex-1 w-1/2 h-full px-3 py-1 text-white text-sm font-medium border-indigo-800 bg-[hsla(260,91%,18%,0.8)] hover:bg-[hsla(260,91%,25%,0.8)] active:hover:bg-[hsla(260,91%,25%)] transition-colors`}
            >
            {`Node to\nFront`}
          </button>
        </div>

      </div>
    </div>
  );

  // Render edge editing controls
  const renderEdgeEditor = () => (
    <div className="flex flex-row">
      <div className={`text-sm w-[32em] h-full pb-4 space-y-2 border-r border-y border-[rgba(75,85,99,0.8)] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} ${styles.effects.boxShadow} rounded-tl-lg shadow-xl`}>
        <h3 className="py-1 text-sm w-full text-center font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.8)]">Connection Styling</h3>

        <div className="px-2 space-y-2">
          {/* Connector Type */}
          <FormField
            type="select"
            label="Connector"
            value={newEdgeSettings?.data?.connectorType || ''}
            onChange={(value) => handleEdgeChange('connectorType', value)}
            options={connectorTypes.map(({ label, value }) => ({ label, value }))}
          />

          {/* Connection Weight */}
          <FormField
            type="range"
            label="Weight"
            value={newEdgeSettings.style?.strokeWidth !== undefined ? parseInt(newEdgeSettings.style?.strokeWidth) : 0}
            onChange={(value) => handleEdgeChange('style.strokeWidth', parseInt(value))}
            min={1}
            max={20}
            step={1}
            unit=""
            className="w-full"
          />

          {/* Connection Style (Solid/Dashed) */}
          <FormField
            type="range"
            label="Dash"
            value={newEdgeSettings.style?.strokeDasharray !== undefined ? parseInt(newEdgeSettings.style?.strokeDasharray) : 0}
            onChange={(value) => handleEdgeChange('style.strokeDasharray', parseInt(value))}
            min={0}
            max={40}
            step={1}
            disabled={newEdgeSettings.animated}
            unit=""
            className="w-full"
          />

          {/* Connection Animation */}
          <FormField
            type="checkbox"
            label="Animate"
            value={newEdgeSettings.animated || false}
            onChange={(value) => handleEdgeChange('animated', value)}
          />

          {/* Marker Type */}
          <FormField
            type="select"
            label="Marker Type"
            value={newEdgeSettings.markerEnd?.type || ''}
            onChange={(value) => handleEdgeChange('markerEnd.type', value)}
            options={markerTypes.map(({ label, value }) => ({ label, value }))}
          />

          {/* Marker Size */}
          <FormField
            type="range"
            label="Marker Size"
            value={newEdgeSettings?.markerEnd?.width !== undefined ? parseInt(newEdgeSettings?.markerEnd?.width) : 10}
            onChange={(value) => handleEdgeChange('markerEnd.size', parseInt(value))}
            min={0}
            max={100}
            step={1}
            unit=""
            className="w-full"
          />
        </div>
      </div>

      <div className={`pb-2 border-r border-y rounded-br-md border-[rgba(75,85,99,0.6)] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} shadow-xl`}>
        <h3 className="py-1 text-sm w-full text-center font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.5)]">Colors</h3>
        {/* Colors Section */}
        <div className="pt-2 w-72 h-full space-y-3">
          <div className="px-2 space-y-2">
            {/* Connection Color */}
            <ColorPickerButton
              colorOptions={colorOptions}
              handleChange={handleEdgeChange}
              property="style.stroke"
              displayName="Stroke Color"
              currentColor={newEdgeSettings.style?.stroke}
            />

            {/* Marker Color */}
            <ColorPickerButton
              colorOptions={colorOptions}
              handleChange={handleEdgeChange}
              property="markerEnd.color"
              displayName="Marker Color"
              currentColor={newEdgeSettings.markerEnd?.color || '#cccccc'}
            />
          </div>
        </div>
      </div>

      {/* Copy / Paste Style */}
      <div className={`pb-2 border-r border-y rounded-br-md border-[rgba(75,85,99,0.6)] ${styles.effects.backdropBlur} ${styles.effects.gradientBg} shadow-xl`}>
        <h3 className="py-1 text-sm w-full text-center font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.5)]">Clipboard</h3>
        {/* Colors Section */}
        <div className={`flex flex-col h-full flex-grow`}>
          <button
            // onClick={() => handleCopyStyle("", selectedEdge, setCopiedStyle) }
            onClick={() => handleCopyStyle("", selectedEdge, setCopiedStyle, null, newEdgeSettings)}
            className="h-20 px-3 text-white text-sm font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.8)] hover:bg-[#2c067abb] active:bg-[#3e08aa] transition-colors"
            >
            Copy Style
          </button>
          <button
            // onClick={() => handlePasteEdgeStyle(selectedEdge, copiedStyle, onUpdateEdge)} 
            onClick={() => handlePasteEdgeStyle(selectedEdge, copiedStyle, onUpdateEdge, setNewEdgeSettings)} 
            className="h-20 px-3 text-white text-sm font-medium border-b border-indigo-800 bg-[rgba(32,4,87,0.8)] hover:bg-[#2c067abb] active:bg-[#3e08aa] transition-colors"
            >
            Paste Style
          </button>
        </div>
      </div>

    </div>
  );

  // Render the appropriate editor based on selection
  return (
    <div className="text-gray-100 backdrop-blur-sm">
      <div className="flex justify-between relative">
        {(selectedNode || selectedEdge) && (
          <>
            <button
              onClick={handleUndo}
              // className="absolute right-2 top-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium border border-gray-500"
              className={`
                absolute right-1 top-1.5 px-3 py-1 rounded-md text-sm font-medium border border-purple-700
                ${nodePropertyChanges || edgePropertyChanges 
                  ? 'bg-[hsl(274,87%,15%)] hover:bg-[hsl(274,87%,22%)] active:bg-[hsl(274,87%,28%)]' 
                  : 'bg-gray-700 opacity-50 cursor-not-allowed'
                }
              `}          
            >
              Revert Changes
            </button>
          </>
        )}

      </div>

      {selectedNode ? (
        renderNodeEditor()
      ) : selectedEdge ? (
        renderEdgeEditor()
      ) : (
        <div className="p-2 text-gray-400 font-medium">No node or edge selected.</div>
      )}
    </div>
  );
};

export default NodeEditor;